import React, { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import {
    defaultColDef,
    intermediaryAccountsColumnDefs,
    isAccountNameValid,
    isEveryElementUnique,
} from "./column_defs";
import styles from "./styles.module.scss";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import Button from "src/components/common/Button";
import { ModalActions } from "src/redux/Modal/actions";
import isEmpty from "is-empty";
import {
    isKsdAccountNoValid,
    isKsfcAccountNoValid,
} from "src/utils/validators";
import TruTooltip from "src/components/common/AgGrid/TruTooltip";
import { SettingsActions } from "src/redux/Settings/actions";
import { ConfirmActions } from "src/redux/Confirm/actions";

class DeleteButton {
    init(params) {
        this.eGui = document.createElement("div");
        this.eGui.className = styles.deleteBtn;
        this.element = document.createElement("div");
        this.element.className = `${styles.delete} delete-btn`;
        // setAttribute keeps the (user-authored) account name inert - never parsed as HTML.
        this.element.setAttribute("data-testid", `delete-${params.data?.name}`);
        this.element.onclick = () => this.handleDelete(params);
        this.eGui.appendChild(this.element);
    }

    handleDelete(params) {
        params.context.deleteRow(params.rowIndex, params.data);
    }

    getGui() {
        return this.eGui;
    }
}

const AccountSettingsV2 = () => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);

    /**
     * States
     */
    const savedAccounts = useSelector(SettingsSelectors.GeneralAccountSettings);
    const funds = useSelector(SettingsSelectors.FundSettings);
    const lists = useSelector(SettingsSelectors.ListSettings);

    const [accounts, setAccounts] = useState(savedAccounts);
    const [gridApi, setGridApi] = useState(null);
    const [shouldRefresh, setShouldRefresh] = useState(false);

    /**
     * State derived constants
     */
    const allAccountNames = useMemo(
        () => accounts.map((a) => a.name),
        [accounts]
    );
    const allAccountCodes = useMemo(
        () => accounts.filter((a) => !isEmpty(a.type)).map((a) => a.accountNo),
        [accounts]
    );

    const errorMessage = useMemo(() => {
        const allKsdAccountCodes = accounts
            .filter((a) => a.type === "KSD")
            .map((a) => a.accountNo);
        const allKsfcAccountCodes = accounts
            .filter((a) => a.type === "KSFC")
            .map((a) => a.accountNo);

        if (!allAccountNames.every(isAccountNameValid)) {
            return "Account name should be 1 to 50 characters long.";
        } else if (!isEveryElementUnique(allAccountNames)) {
            return "Account name must be unique.";
        } else if (!accounts.every((a) => !isEmpty(a.type))) {
            return "Intermediary should be specified.";
        } else if (!allAccountCodes.every((c) => !isEmpty(c))) {
            return "Account code cannot be empty.";
        } else if (!isEveryElementUnique(allAccountCodes)) {
            return "Duplicate account code found.";
        } else if (!allKsdAccountCodes.every(isKsdAccountNoValid)) {
            return "KSD account code must be 10 numbers long.";
        } else if (!allKsfcAccountCodes.every(isKsfcAccountNoValid)) {
            return "KSFC account code must be 12 numbers long.";
        }
        return "";
    }, [accounts, allAccountNames, allAccountCodes]);

    /**
     * Event handlers
     */
    const handleGridReady = (params) => setGridApi(params.api);

    const handleRowDataChanged = useCallback(() => {
        if (gridApi && shouldRefresh) {
            const index = gridApi.getDisplayedRowCount() - 1;
            gridApi.ensureIndexVisible(index, "bottom");
            setShouldRefresh(false);
        }
    }, [gridApi, shouldRefresh]);

    const handleAddNewRow = useCallback(() => {
        setAccounts(
            accounts.concat({
                name: "",
                type: "",
                accountNo: "",
                isNew: true,
            })
        );

        setShouldRefresh(true);
    }, [accounts]);

    const handleDeleteRow = useCallback(
        (rowIndex, rowData) => {
            let affectedFunds;
            let affectedLists;

            switch (rowData.type) {
                case "KSD":
                    affectedFunds = funds.filter(
                        (f) => f.ksdAccount?.accountNo === rowData.accountNo
                    );
                    affectedLists = lists.filter(
                        (l) => l.ksdAccount?.accountNo === rowData.accountNo
                    );
                    break;

                case "KSFC":
                    affectedFunds = funds.filter(
                        (f) => f.ksfcAccount?.accountNo === rowData.accountNo
                    );
                    affectedLists = lists.filter(
                        (l) => l.ksfcAccount?.accountNo === rowData.accountNo
                    );
                    break;

                default:
                    throw new Error(
                        "Unhandled intermediary type: " + rowData.type
                    );
            }

            if (
                rowData.isNew ||
                (isEmpty(affectedFunds) && isEmpty(affectedLists))
            ) {
                return setAccounts(
                    // preserves immutability
                    accounts
                        .slice(0, rowIndex)
                        .concat(accounts.slice(rowIndex + 1))
                );
            }

            const message = `Following funds/lists will be affected:\n${affectedFunds
                .map((f) => f.fundName)
                .concat(affectedLists.map((l) => l.name))
                .map((name) => `- ${name}`)
                .join("\n")}\nAre you sure you want to delete this account?`;

            return dispatch(
                ConfirmActions.Open({
                    message,
                    title: "Delete",
                    cancelTitle: "Cancel",
                    okTitle: "Ok",
                    testId: "account-remove",
                    backdrop: true,
                    onOk: () =>
                        setAccounts(
                            accounts
                                .slice(0, rowIndex)
                                .concat(accounts.slice(rowIndex + 1))
                        ),
                })
            );
        },
        [dispatch, funds, lists, accounts]
    );

    const handleCellValueChanged = useCallback(
        (params) => {
            let tempAccounts = [...accounts];
            tempAccounts.splice(params.rowIndex, 1, { ...params.data });
            setAccounts(tempAccounts);
            params.api.redrawRows();
        },
        [accounts]
    );

    const handleSave = useCallback(() => {
        const accountsToSave = accounts.map((a) => {
            const { isNew, ...rest } = a;
            return rest;
        });

        // Exit settings if no changes are made
        if (JSON.stringify(accountsToSave) === JSON.stringify(savedAccounts)) {
            return dispatch(ModalActions.Close());
        }

        dispatch(SettingsActions.SetGeneralAccountSettings(accountsToSave));
        dispatch(SettingsActions.SaveCPTYSettingsForEveryRole());
    }, [accounts, dispatch, savedAccounts]);

    return (
        <div className={styles.intermediaryAccountsWrapper}>
            <div
                className={styles.intermediaryAccountsTable}
                data-testid="intermediary-accounts"
            >
                <div
                    className={[styles.table, "ag-theme-alpine-dark"].join(" ")}
                >
                    <AgGridReact
                        rowBuffer={50}
                        rowHeight={40}
                        headerHeight={40}
                        rowData={accounts}
                        defaultColDef={defaultColDef}
                        columnDefs={intermediaryAccountsColumnDefs}
                        suppressContextMenu
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                        suppressHorizontalScroll
                        enableCellChangeFlash
                        components={{
                            dropdown: DropdownItemRenderer,
                            deleteButton: DeleteButton,
                            txTooltip: TruTooltip,
                        }}
                        context={{
                            deleteRow: handleDeleteRow,
                            allAccountNames,
                            allAccountCodes,
                        }}
                        tooltipShowDelay={1}
                        onGridReady={handleGridReady}
                        onRowDataChanged={handleRowDataChanged}
                        onCellValueChanged={handleCellValueChanged}
                    />
                </div>
            </div>
            <div className={styles.controls}>
                {!isEmpty(errorMessage) && (
                    <div
                        className={styles.errorMessage}
                        data-testid={"account-error-message"}
                    >
                        {errorMessage}
                    </div>
                )}
            </div>
            <div className={styles.bottomContainer}>
                <Button
                    dataTestId="add-new-account"
                    value="Add Account"
                    color="reversedWhite"
                    disabled={!isEmpty(errorMessage)}
                    onClick={handleAddNewRow}
                />

                <div className={styles.bottom}>
                    <Button
                        onClick={() => dispatch(ModalActions.Close())}
                        color="reversedWhite"
                        dataTestId="cancel-save"
                        value="Cancel"
                    />
                    <Button
                        onClick={handleSave}
                        dataTestId="save-account-details"
                        value="Save"
                        disabled={!isTraderRole || !isEmpty(errorMessage)}
                    />
                </div>
            </div>
        </div>
    );
};

export default AccountSettingsV2;
